import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PublicSession, SessionUsage, UploadFileResult } from '@wallflower/shared';
import { MAX_MESSAGE_LENGTH, remainingAllowance } from '@wallflower/shared';
import { ApiError, api, uploadFiles } from '../lib/api.ts';
import { useSessionSocket } from '../lib/useSessionSocket.ts';
import { formatCountdown, useCountdown } from '../lib/useCountdown.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

const NAME_KEY = 'wallflower:name';

interface Sent {
  id: string;
  previewUrl: string;
  name: string;
}

/**
 * One thing at a time: pick photo or message, do it, then get the choice back.
 * Guests are holding a phone in a dark room, often after a drink.
 */
type View = 'choose' | 'photo' | 'message' | 'done';

export default function Upload() {
  const { sessionId = '' } = useParams();
  useDocumentTitle('Upload');
  const [session, setSession] = useState<PublicSession | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [view, setView] = useState<View>('choose');
  const [lastAction, setLastAction] = useState<'photo' | 'message' | null>(null);

  const [progress, setProgress] = useState<number | null>(null);
  const [sent, setSent] = useState<Sent[]>([]);
  const [rejected, setRejected] = useState<UploadFileResult[]>([]);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [messageError, setMessageError] = useState<string | null>(null);

  const pickerRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  // Kept for the settings alone: the host may turn video or messages off while
  // a guest has this page open, and the choices should follow.
  const live = useSessionSocket({ sessionId });

  useEffect(() => {
    api
      .publicSession(sessionId)
      .then(setSession)
      .catch(() => setLoadError('This upload link is not valid any more.'));
  }, [sessionId]);

  useEffect(() => {
    return () => sent.forEach((item) => URL.revokeObjectURL(item.previewUrl));
  }, [sent]);

  const settings = live.session?.settings ?? session?.settings;
  const allowVideo = settings?.allowVideo ?? false;
  const maxVideoSeconds = settings?.maxVideoSeconds ?? 10;
  const allowMessages = settings?.allowMessages ?? false;

  const current = live.session ?? session;
  const limits = current?.limits ?? { media: null, messages: null };
  const serverUsage = current?.usage;
  /**
   * The server is the authority, but it is a round trip behind: other guests are
   * spending the same allowance, and our own sends land before the next `hello`.
   * Taking the larger of the two never promises room that isn't there.
   */
  const [usage, setUsage] = useState<SessionUsage>({ media: 0, messages: 0 });
  useEffect(() => {
    if (!serverUsage) return;
    setUsage((prev) => ({
      media: Math.max(prev.media, serverUsage.media),
      messages: Math.max(prev.messages, serverUsage.messages),
    }));
  }, [serverUsage]);

  const photosLeft = remainingAllowance(limits.media, usage.media);
  const messagesLeft = remainingAllowance(limits.messages, usage.messages);

  const remainingMs = useCountdown(current?.plan === 'demo' ? current.expiresAt : null);
  const [ended, setEnded] = useState(false);
  const isOver = ended || live.status === 'gone' || remainingMs === 0;

  // With messages off there is only one thing to do, so skip the fork entirely.
  useEffect(() => {
    if (!allowMessages && view === 'choose' && session) setView('photo');
  }, [allowMessages, view, session]);

  const rememberName = () => {
    const trimmed = name.trim();
    localStorage.setItem(NAME_KEY, trimmed);
    return trimmed;
  };

  async function sendMessage() {
    const text = draft.trim();
    if (!text || !session) return;

    setSending(true);
    setMessageError(null);
    try {
      const result = await api.postMessage(session.id, text, rememberName() || null);
      setUsage(result.usage);
      setDraft('');
      setLastAction('message');
      setView('done');
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setEnded(true);
      } else if (err instanceof ApiError && err.status === 403) {
        // Someone else spent the last of it while this one was being typed.
        setUsage((prev) => ({ ...prev, messages: limits.messages ?? prev.messages }));
        setMessageError(err.message);
      } else {
        setMessageError('That message did not go through. Try again?');
      }
    } finally {
      setSending(false);
    }
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || !session) return;

    const accepted: File[] = [];
    const refused: UploadFileResult[] = [];

    // Refuse the overflow here rather than sending bytes the server will reject.
    let room = photosLeft ?? Number.POSITIVE_INFINITY;

    for (const file of Array.from(files)) {
      if (room <= 0) {
        refused.push({
          originalName: file.name,
          ok: false,
          reason: 'limit_reached',
          message: `This wall holds ${limits.media} photos, and they are all in`,
        });
        continue;
      }
      if (file.type.startsWith('video/')) {
        if (!allowVideo) {
          refused.push({
            originalName: file.name,
            ok: false,
            reason: 'video_not_allowed',
            message: 'Videos are turned off for this event',
          });
          continue;
        }
        // Check duration here so a guest on mobile data doesn't upload a clip
        // the server is only going to reject.
        const seconds = await videoDuration(file).catch(() => 0);
        if (seconds > maxVideoSeconds + 0.5) {
          refused.push({
            originalName: file.name,
            ok: false,
            reason: 'too_long',
            message: `Clips need to be ${maxVideoSeconds} seconds or shorter`,
          });
          continue;
        }
      }
      accepted.push(file);
      room -= 1;
    }

    setRejected(refused);
    if (accepted.length === 0) return;

    setProgress(0);
    try {
      const response = await uploadFiles(session.id, accepted, rememberName() || null, setProgress);
      setUsage(response.usage);
      const failures = response.results.filter((r) => !r.ok);
      setRejected((prev) => [...prev, ...failures]);

      const succeeded = accepted.filter((file) =>
        response.results.some((r) => r.originalName === file.name && r.ok),
      );
      setSent((prev) => [
        ...succeeded.map((file) => ({
          id: `${file.name}-${file.lastModified}`,
          previewUrl: URL.createObjectURL(file),
          name: file.name,
        })),
        ...prev,
      ]);

      if (succeeded.length > 0) {
        setLastAction('photo');
        setView('done');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setEnded(true);
      } else {
        setRejected((prev) => [
          ...prev,
          { originalName: 'Upload', ok: false, message: 'That did not go through. Try again?' },
        ]);
      }
    } finally {
      setProgress(null);
      if (pickerRef.current) pickerRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  }

  if (loadError) {
    return (
      <main className="grid flex-1 place-items-center px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-semibold">Link expired</h1>
          <p className="mt-3 text-muted">{loadError}</p>
        </div>
      </main>
    );
  }

  /**
   * An unpaid wall has real limits of zero, so the upload and message routes will
   * refuse anything. Saying "this wall is full" would be a lie; a guest who tries
   * and is turned away has no idea it is the host who has something left to do.
   * The socket sends `plan:updated` the moment it is paid for, and this clears.
   */
  if (current?.plan === 'unpaid') {
    return (
      <main className="grid flex-1 place-items-center px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-semibold">This wall isn't live yet</h1>
          <p className="mt-3 text-muted">
            {current.name} is set up, but not open for photos yet. Have another scan of the code
            in a little while.
          </p>
        </div>
      </main>
    );
  }

  if (isOver) {
    return (
      <main className="grid flex-1 place-items-center px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-semibold">This wall has ended</h1>
          <p className="mt-3 text-muted">
            {current?.plan === 'demo'
              ? 'Demo walls run for a few minutes, then everything on them is deleted.'
              : 'The event it belonged to is no longer available.'}
          </p>
          <Link className="btn btn-primary mt-6" to="/">
            Make one of your own
          </Link>
        </div>
      </main>
    );
  }

  const busy = progress !== null;
  const goChoose = () => {
    setRejected([]);
    setMessageError(null);
    setView(allowMessages ? 'choose' : 'photo');
  };

  return (
    <main className="mx-auto flex w-full max-w-md flex-1 flex-col px-5 py-10">
      {/* Above the heading, where a back control belongs. */}
      {(view === 'message' || (view === 'photo' && allowMessages)) && !busy ? (
        <BackButton onClick={goChoose} />
      ) : null}

      <header>
        <p className="eyebrow">{session?.name ?? 'Loading…'}</p>
        <h1 className="font-display mt-2 text-4xl leading-tight font-semibold">
          {view === 'choose'
            ? 'Add to the big screen.'
            : view === 'photo'
              ? 'Send a photo.'
              : view === 'message'
                ? 'Say something.'
                : lastAction === 'message'
                  ? 'On the ticker.'
                  : 'On the screen.'}
        </h1>
      </header>

      {remainingMs !== null ? (
        <p className="mt-4 flex items-baseline gap-2 text-sm text-muted">
          <span className="eyebrow">Demo wall</span>
          <span className="font-display text-sulphur">{formatCountdown(remainingMs)} left</span>
          <span>then everything on it is deleted</span>
        </p>
      ) : null}

      {view === 'choose' ? (
        <div className="mt-8 grid gap-3">
          <BigButton
            onClick={() => setView('photo')}
            disabled={!session || photosLeft === 0}
            tone="primary"
          >
            Add a photo
            <Sub>
              {photosLeft === 0
                ? `All ${limits.media} photos are in`
                : photosLeft !== null
                  ? `${photosLeft} of ${limits.media} left`
                  : allowVideo
                    ? `Or a clip up to ${maxVideoSeconds} seconds`
                    : 'From your camera roll or the camera'}
            </Sub>
          </BigButton>
          <BigButton onClick={() => setView('message')} disabled={!session || messagesLeft === 0}>
            Add a message
            <Sub>
              {messagesLeft === 0
                ? `All ${limits.messages} messages are in`
                : messagesLeft !== null
                  ? `${messagesLeft} of ${limits.messages} left`
                  : 'Scrolls along the bottom of the screen'}
            </Sub>
          </BigButton>
        </div>
      ) : null}

      {view === 'photo' ? (
        <>
          <NameField name={name} onChange={setName} />

          {photosLeft !== null ? (
            <p className="mt-4 text-sm text-muted">
              {photosLeft === 0
                ? `This wall holds ${limits.media} photos, and they are all in.`
                : `Room for ${photosLeft} more photo${photosLeft === 1 ? '' : 's'} on this wall.`}
            </p>
          ) : null}

          <div className="mt-5 grid gap-3">
            <button
              className="btn btn-primary py-4 text-base"
              onClick={() => pickerRef.current?.click()}
              disabled={busy || !session || photosLeft === 0}
            >
              {busy ? `Sending… ${Math.round((progress ?? 0) * 100)}%` : 'Choose from your photos'}
            </button>
            <button
              className="btn btn-ghost py-4 text-base"
              onClick={() => cameraRef.current?.click()}
              disabled={busy || !session || photosLeft === 0}
            >
              Take a photo now
            </button>
          </div>

          {busy ? (
            <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-raised">
              <div
                className="h-full rounded-full bg-petal transition-[width] duration-200"
                style={{ width: `${Math.round((progress ?? 0) * 100)}%` }}
              />
            </div>
          ) : null}
        </>
      ) : null}

      {view === 'message' ? (
        <>
          <NameField name={name} onChange={setName} />

          <div className="mt-5">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="eyebrow">Your message</span>
              <span className="text-xs text-muted">{MAX_MESSAGE_LENGTH - draft.length} left</span>
            </div>
            <textarea
              className="field resize-none"
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
              placeholder="Congratulations you two!"
              maxLength={MAX_MESSAGE_LENGTH}
              autoFocus
            />
            <button
              className="btn btn-primary mt-3 w-full py-4 text-base"
              onClick={() => void sendMessage()}
              disabled={sending || !draft.trim() || !session || messagesLeft === 0}
            >
              {sending ? 'Sending…' : 'Send to the screen'}
            </button>
            {messagesLeft !== null ? (
              <p className="mt-2 text-sm text-muted">
                {messagesLeft === 0
                  ? `This wall holds ${limits.messages} messages, and they are all in.`
                  : `Room for ${messagesLeft} more message${messagesLeft === 1 ? '' : 's'} on this wall.`}
              </p>
            ) : null}
            {messageError ? (
              <p role="alert" className="mt-2 text-sm text-danger">
                {messageError}
              </p>
            ) : null}
          </div>
        </>
      ) : null}

      {view === 'done' ? (
        <>
          <p className="mt-3 text-muted">
            {lastAction === 'message'
              ? 'It will come round on the screen in a moment.'
              : 'It appears on the screen within a few seconds.'}
          </p>

          {lastAction === 'photo' && sent.length > 0 ? (
            <ul className="mt-6 grid grid-cols-3 gap-2">
              {sent.slice(0, 6).map((item) => (
                <li
                  key={item.id}
                  className="aspect-square overflow-hidden rounded-lg border border-edge"
                >
                  <img src={item.previewUrl} alt={item.name} className="h-full w-full object-cover" />
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-8 grid gap-3">
            <BigButton onClick={() => setView('photo')} tone="primary" disabled={photosLeft === 0}>
              Add a photo
              {photosLeft !== null ? (
                <Sub>
                  {photosLeft === 0 ? `All ${limits.media} photos are in` : `${photosLeft} left`}
                </Sub>
              ) : null}
            </BigButton>
            {allowMessages ? (
              <BigButton onClick={() => setView('message')} disabled={messagesLeft === 0}>
                Add a message
                {messagesLeft !== null ? (
                  <Sub>
                    {messagesLeft === 0
                      ? `All ${limits.messages} messages are in`
                      : `${messagesLeft} left`}
                  </Sub>
                ) : null}
              </BigButton>
            ) : null}
          </div>
        </>
      ) : null}

      <input
        ref={pickerRef}
        type="file"
        accept={allowVideo ? 'image/*,video/*' : 'image/*'}
        multiple
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {rejected.length > 0 ? (
        <ul className="mt-6 grid gap-2">
          {rejected.map((item, index) => (
            <li key={`${item.originalName}-${index}`} className="panel p-3 text-sm">
              <span className="text-danger">{item.message ?? 'Could not be added'}</span>
              <span className="block text-xs text-muted">{item.originalName}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="mt-auto pt-10 text-xs text-muted">
        Anything you send shows on the event screen and can be removed by the host.
      </p>
    </main>
  );
}

function BigButton({
  onClick,
  disabled,
  tone = 'ghost',
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone?: 'primary' | 'ghost';
  children: React.ReactNode;
}) {
  return (
    <button
      className={`btn ${tone === 'primary' ? 'btn-primary' : 'btn-ghost'} flex-col gap-1 px-5 py-6 text-lg`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-normal opacity-70">{children}</span>;
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="mb-4 self-start text-sm text-muted hover:text-chalk" onClick={onClick}>
      ← Back
    </button>
  );
}

/** One name for both photos and messages, remembered between visits. */
function NameField({ name, onChange }: { name: string; onChange: (value: string) => void }) {
  return (
    <label className="mt-5 grid gap-2">
      <span className="eyebrow">Your name (optional)</span>
      <input
        className="field"
        value={name}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Shown on the screen"
        maxLength={60}
        autoComplete="name"
      />
    </label>
  );
}

function videoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const element = document.createElement('video');
    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      URL.revokeObjectURL(element.src);
      resolve(element.duration);
    };
    element.onerror = () => {
      URL.revokeObjectURL(element.src);
      reject(new Error('Could not read that video'));
    };
    element.src = URL.createObjectURL(file);
  });
}
